<template>
  <BenefitsCollapsibleSection :title="t('benefitsCenter-faq-title')" :default-open="false">
    <div class="benefits-faq-section">
      <div class="benefits-faq-intro">
        <div class="benefits-faq-intro-field">
          <div class="benefits-faq-intro-header">
            <span>{{ t("benefitsCenter-faq-introTitle") }}</span>

            <BenefitsToggle v-model="intro.showTitle" :aria-label="t('benefitsCenter-faq-introShowTitle')"
              @input="saveIntro" />
          </div>

          <KvInput
            v-if="intro.showTitle"
            v-model="intro.title"
            variant="outlined"
            size="large"
            :placeholder="t('benefitsCenter-faq-introTitlePlaceholder')"
            @input="saveIntroDebounced"
          />
        </div>

        <div class="benefits-faq-intro-field">
          <div class="benefits-faq-intro-header">
            <span>{{ t("benefitsCenter-faq-introImage") }}</span>

            <BenefitsToggle v-model="intro.showImage" :aria-label="t('benefitsCenter-faq-introShowImage')"
              @input="saveIntro" />
          </div>

          <KvImageInput
            v-if="intro.showImage"
            v-model="intro.image"
            variant="outlined"
            size="large"
            :title="t('benefitsCenter-faq-introImageTitle')"
            :description="t('benefitsCenter-faq-introImageDescription')"
            :button-label="t('benefitsCenter-faq-introImageButton')"
            :delete-label="t('benefitsCenter-faq-introImageDelete')"
            @input="saveIntro"
          />
        </div>

        <div class="benefits-faq-intro-field">
          <div class="benefits-faq-intro-header">
            <span>{{ t("benefitsCenter-faq-introText") }}</span>

            <BenefitsToggle v-model="intro.showIntro" :aria-label="t('benefitsCenter-faq-introShowText')"
              @input="saveIntro" />
          </div>

          <BenefitsRichText v-if="intro.showIntro" v-model="intro.intro"
            :placeholder="t('benefitsCenter-faq-introTextPlaceholder')" height="160px" @input="saveIntroDebounced" />
        </div>
      </div>

      <KvButton class="benefits-faq-add-button" type="button" variant="outlined" @click="openCreatePanel">
        <Icon icon-class="ui-plus" size="5" color="#1f2a37" />
        {{ t("benefitsCenter-faq-addQuestion") }}
      </KvButton>

      <div v-if="normalizedItems.length" class="benefits-faq-list">
        <article v-for="(question, index) in normalizedItems" :key="getItemKey(question, index)" class="benefits-faq-item">
          <button class="benefits-faq-question-button" type="button" :aria-label="t('benefitsCenter-faq-editQuestion')"
            :title="t('benefitsCenter-faq-editQuestion')" @click="openEditPanel(index)">
            <span>{{ question.question }}</span>
          </button>

          <button class="benefits-faq-item-action benefits-faq-item-action-danger" type="button"
            :aria-label="t('benefitsCenter-faq-deleteQuestion')" :title="t('benefitsCenter-faq-deleteQuestion')"
            @click.stop="openDeleteConfirm(index)">
            <Icon icon-class="ui-trash-01" size="4" color="#f04438" />
          </button>
        </article>
      </div>

    </div>

    <BenefitsSidePanel v-model="panelOpen" :title="panelTitle" :close-label="t('benefitsCenter-faq-close')"
      @close="resetDraft">
      <BenefitsFaqForm v-model="draft" :question-label="t('benefitsCenter-faq-question')"
        :answer-label="t('benefitsCenter-faq-answer')"
        :question-placeholder="t('benefitsCenter-faq-questionPlaceholder')"
        :answer-placeholder="t('benefitsCenter-faq-answerPlaceholder')" />

      <template #footer>
        <KvButton class="benefits-faq-close-button" type="button" variant="outlined" @click="closePanel">
          {{ t("benefitsCenter-faq-close") }}
        </KvButton>

        <KvButton class="benefits-faq-save-button" type="button" :disabled="!canSave" @click="saveQuestion">
          <Icon icon-class="ui-check" size="4" color="#ffffff" />
          {{ t("benefitsCenter-faq-saveChanges") }}
        </KvButton>
      </template>
    </BenefitsSidePanel>

    <div v-if="deleteConfirmOpen" class="benefits-faq-modal-backdrop" @click.self="closeDeleteConfirm">
      <KvCard class="benefits-faq-delete-modal" content-class="benefits-faq-delete-modal-content">
        <template #text>
          <h4>{{ t("benefitsCenter-faq-deleteModalTitle") }}</h4>
          <p>{{ t("benefitsCenter-faq-deleteModalDescription") }}</p>

          <div class="benefits-faq-delete-modal-actions">
            <KvButton class="benefits-faq-delete-cancel-button" type="button" variant="outlined" @click="closeDeleteConfirm">
              {{ t("benefitsCenter-faq-deleteModalCancel") }}
            </KvButton>

            <KvButton class="benefits-faq-delete-confirm-button" type="button" variant="outlined" @click="confirmDeleteQuestion">
              {{ t("benefitsCenter-faq-deleteModalConfirm") }}
            </KvButton>
          </div>
        </template>
      </KvCard>
    </div>
  </BenefitsCollapsibleSection>
</template>

<script>
import Icon from "../../../components/icon/icon";
import KvButton from "../../../components/form/kv-button.vue";
import KvCard from "../../../components/ui/kv-card.vue";
import BenefitsCollapsibleSection from "../../shared/benefits-collapsible-section.vue";
import BenefitsSidePanel from "../../shared/benefits-side-panel.vue";
import BenefitsFaqForm from "./benefits-faq-form.vue";
import BenefitsRichText from "../../shared/benefits-rich-text.vue";
import KvImageInput from "../../../components/inputs/kv-image-input.vue";
import BenefitsToggle from "../../shared/benefits-toggle.vue";
import KvInput from "../../../components/inputs/kv-input.vue";

export default {
  name: "BenefitsFaqSection",
  components: {
    Icon,
    KvButton,
    KvCard,
    BenefitsCollapsibleSection,
    BenefitsSidePanel,
    BenefitsFaqForm,
    BenefitsRichText,
    KvImageInput,
    BenefitsToggle,
    KvInput,
  },
  model: {
    prop: "value",
    event: "input",
  },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      panelOpen: false,
      deleteConfirmOpen: false,
      editingIndex: null,
      pendingDeleteIndex: null,
      draft: this.getEmptyDraft(),
      intro: this.getIntro(this.value),
      introTimer: null,
    };
  },
  watch: {
    value: {
      deep: true,
      handler(value) {
        if (!this.introTimer) {
          this.intro = this.getIntro(value);
        }
      },
    },
  },
  beforeDestroy() {
    clearTimeout(this.introTimer);
  },
  computed: {
    normalizedItems() {
      return Array.isArray(this.value && this.value.items) ? this.value.items : [];
    },
    panelTitle() {
      return this.editingIndex === null
        ? this.t("benefitsCenter-faq-addQuestionTitle")
        : this.t("benefitsCenter-faq-editQuestionTitle");
    },
    canSave() {
      return Boolean(String(this.draft.question || "").trim());
    },
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    getEmptyDraft() {
      return {
        question: "",
        answer: "",
      };
    },
    getIntro(value) {
      const source = value && typeof value === "object" ? value : {};

      return {
        title: source.title || "",
        showTitle: source.showTitle !== false,
        image: source.image || null,
        showImage: source.showImage !== false,
        intro: source.intro || "",
        showIntro: source.showIntro !== false,
      };
    },
    emitFaq(items) {
      this.$emit("input", {
        ...this.intro,
        items: Array.isArray(items) ? items : this.normalizedItems,
      });
    },
    saveIntro() {
      clearTimeout(this.introTimer);
      this.introTimer = null;
      this.emitFaq();
    },
    saveIntroDebounced() {
      clearTimeout(this.introTimer);
      this.introTimer = setTimeout(() => {
        this.introTimer = null;
        this.emitFaq();
      }, 450);
    },
    getItemKey(item, index) {
      return `faq-${index}`;
    },
    openCreatePanel() {
      this.editingIndex = null;
      this.draft = this.getEmptyDraft();
      this.panelOpen = true;
    },
    openEditPanel(index) {
      const item = this.normalizedItems[index] || this.getEmptyDraft();

      this.editingIndex = index;
      this.draft = {
        question: item.question || "",
        answer: item.answer || "",
      };
      this.panelOpen = true;
    },
    closePanel() {
      this.panelOpen = false;
      this.resetDraft();
    },
    resetDraft() {
      this.editingIndex = null;
      this.draft = this.getEmptyDraft();
    },
    saveQuestion() {
      if (!this.canSave) {
        this.$store.dispatch("notification/error", {
          text: this.t("benefitsCenter-faq-emptyError"),
        });
        return;
      }

      try {
        const nextItem = {
          question: this.draft.question.trim(),
          answer: String(this.draft.answer || "").trim(),
        };

        const nextItems = [...this.normalizedItems];
        const isNew = this.editingIndex === null;

        if (isNew) {
          nextItems.push(nextItem);
        } else {
          nextItems.splice(this.editingIndex, 1, nextItem);
        }

        this.emitFaq(nextItems);
        this.closePanel();
      } catch (error) {
        this.$store.dispatch("notification/error", {
          text: this.t("benefitsCenter-faq-saveError"),
        });
      }
    },
    openDeleteConfirm(index) {
      this.pendingDeleteIndex = index;
      this.deleteConfirmOpen = true;
    },
    closeDeleteConfirm() {
      this.pendingDeleteIndex = null;
      this.deleteConfirmOpen = false;
    },
    confirmDeleteQuestion() {
      if (this.pendingDeleteIndex === null) return;

      const indexToDelete = this.pendingDeleteIndex;

      try {
        this.emitFaq(this.normalizedItems.filter((item, itemIndex) => itemIndex !== indexToDelete));
        this.closeDeleteConfirm();
      } catch (error) {
        this.$store.dispatch("notification/error", {
          text: this.t("benefitsCenter-faq-deleteError"),
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.benefits-faq-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: none;
  min-width: 0;
  width: 100%;
}

.benefits-faq-intro {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.benefits-faq-intro-field {
  display: flex;
  flex-direction: column;
  gap: 8px;

  ::v-deep .kv-input {
    width: 100%;
  }

  ::v-deep .kv-editor,
  ::v-deep .kv-image-input {
    width: 100%;
  }
}

.benefits-faq-intro-header {
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: flex-start;

  span {
    color: #384250;
    font-family: "Sora", sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.3;
  }
}

.benefits-faq-add-button {
  align-items: center;
  appearance: none;
  background: #fcfcfd;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #1f2a37;
  cursor: pointer;
  display: inline-flex;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 400;
  gap: 4px;
  min-height: 44px;
  justify-content: center;
  line-height: 1.2;
  padding: 12px;
  transition: background 0.18s ease, border-color 0.18s ease;
  width: 100%;
  overflow-wrap: anywhere;

  &:hover,
  &:focus {
    background: #e5e7eb;
    border-color: #d2d6db;
    outline: none;
  }
}

.benefits-faq-add-button::v-deep .kv-button-content,
.benefits-faq-delete-cancel-button::v-deep .kv-button-content,
.benefits-faq-delete-confirm-button::v-deep .kv-button-content,
.benefits-faq-close-button::v-deep .kv-button-content,
.benefits-faq-save-button::v-deep .kv-button-content {
  gap: inherit;
  line-height: inherit;
}

.benefits-faq-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.benefits-faq-item {
  align-items: center;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  display: flex;
  gap: 12px;
  min-height: 52px;
  padding: 10px 12px;
  position: relative;
  transition: background 0.18s ease, border-color 0.18s ease;
  width: 100%;

  &:hover,
  &:focus-within {
    background: #ffffff;
    border-color: #d2d6db;

    .benefits-faq-item-action-danger {
      opacity: 1;
      pointer-events: auto;
      transform: translateX(0);
    }
  }
}

.benefits-faq-question-button {
  align-items: center;
  appearance: none;
  background: transparent;
  border: 0;
  color: #1f2a37;
  cursor: pointer;
  display: inline-flex;
  flex: 1 1 auto;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 400;
  justify-content: flex-start;
  line-height: 1.3;
  min-width: 0;
  padding: 0 3px;
  text-align: left;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid #2aaa4c;
    outline-offset: 4px;
  }

  span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.benefits-faq-item-action {
  align-items: center;
  appearance: none;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  height: 32px;
  justify-content: center;
  padding: 0;
  transition: background 0.18s ease, opacity 0.18s ease, transform 0.18s ease;
  width: 32px;

  &:hover,
  &:focus {
    outline: none;
  }
}

.benefits-faq-item-action-danger {
  background: #fee4e2;
  opacity: 0;
  pointer-events: none;
  transform: translateX(4px);

  &:hover,
  &:focus {
    background: #fecdca;
    opacity: 1;
    pointer-events: auto;
    transform: translateX(0);
  }
}

.benefits-faq-modal-backdrop {
  align-items: center;
  background: rgba(17, 25, 39, 0.72);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  padding: 16px;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1050;
}

.benefits-faq-delete-modal {
  max-width: 520px;
  width: 100%;
}

.benefits-faq-delete-modal::v-deep .kv-card-content-inner {
  padding: 16px;
}

.benefits-faq-delete-modal h4 {
  color: #4d5761;
  font-family: "Sora", sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  margin: 0 0 12px;
}

.benefits-faq-delete-modal p {
  color: #384250;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.3;
  margin: 0;
}

.benefits-faq-delete-cancel-button,
.benefits-faq-delete-confirm-button {
  align-items: center;
  background: #fcfcfd;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #1f2a37;
  cursor: pointer;
  display: inline-flex;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 400;
  gap: 4px;
  height: 44px;
  justify-content: center;
  line-height: 1.2;
  padding: 12px;
  transition: background 0.15s ease;
  white-space: nowrap;

  &:hover,
  &:focus {
    background: #f9fafb;
    outline: none;
  }
}

.benefits-faq-delete-confirm-button {
  background: #fee4e2;
  border-color: #fee4e2;
  color: #b42318;

  &:hover,
  &:focus {
    background: #fecdca;
  }
}

.benefits-faq-delete-modal-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 24px;
  width: 100%;
}

.benefits-faq-close-button,
.benefits-faq-save-button {
  align-items: center;
  appearance: none;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  gap: 6px;
  height: 36px;
  justify-content: center;
  line-height: 1.2;
  padding: 8px 12px;
}

.benefits-faq-close-button {
  background: #fcfcfd;
  border: 1px solid #e5e7eb;
  color: #1f2a37;
}

.benefits-faq-save-button {
  background: #4caf5d;
  border: 1px solid #4caf5d;
  color: #ffffff;
  margin-left: auto;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

@media (max-width: 700px) {
  .benefits-faq-modal-backdrop {
    align-items: flex-start;
    overflow-y: auto;
  }

  .benefits-faq-delete-cancel-button,
  .benefits-faq-delete-confirm-button {
    white-space: normal;
  }

  .benefits-faq-delete-modal-actions {
    align-items: stretch;
    flex-direction: column-reverse;
  }
}

@media (hover: none),
(max-width: 767px) {
  .benefits-faq-item-action-danger {
    opacity: 1;
    pointer-events: auto;
    transform: translateX(0);
  }

  .benefits-faq-question-button span {
    white-space: normal;
    overflow-wrap: anywhere;
  }
}
</style>
